/**
 * Copyright (c) 2025 George Nassef, Vector Engineering LLC
 * All rights reserved.
 * 
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 * Commercial use requires explicit written permission.
 */

import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import theme from './theme';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import HomeIcon from '@mui/icons-material/Home';
import CreateIcon from '@mui/icons-material/Create';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import PersonIcon from '@mui/icons-material/Person';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useNavigate } from 'react-router-dom';

// Pages
import CreateScriptPage from './pages/CreateScriptPage';
import HomePage from './pages/HomePage';
import ScriptDetailPage from './pages/ScriptDetailPage';
import ScriptsPage from './pages/ScriptsPage';
import CharacterViewerPage from './pages/CharacterViewerPage';

// Context Providers
import { GenerationProvider } from './contexts/GenerationContext';

// Disabled pages (not needed in this phase)
// import VideoGenerationPage from './pages/VideoGenerationPage';


// Main layout with navigation
const Layout = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" color="primary">
        <Toolbar>
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            DramazNow
          </Typography>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
            <Button
              color="inherit"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/')}
            >
              Home
            </Button>
            <Button
              color="inherit"
              startIcon={<CreateIcon />}
              onClick={() => navigate('/create')}
            >
              Create
            </Button>
            <Button
              color="inherit"
              startIcon={<VideoLibraryIcon />}
              onClick={() => navigate('/scripts')}
            >
              My Scripts
            </Button>
            <Button
              color="inherit"
              onClick={() => navigate('/characters')}
            >
              3D Characters
            </Button>
          </Box>

          <IconButton
            size="large"
            edge="end"
            color="inherit"
            aria-label="account"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            sx={{ ml: 2 }}
          >
            <PersonIcon />
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={handleClose}>Profile</MenuItem>
            <MenuItem onClick={handleClose}>Settings</MenuItem>
            <MenuItem onClick={handleClose}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Container component="main" sx={{ flexGrow: 1, py: 4 }}>
        {children}
      </Container>

      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? theme.palette.grey[200]
              : theme.palette.grey[800],
        }}
      >
        <Container maxWidth="sm">
          <Typography variant="body2" color="text.secondary" align="center">
            © {new Date().getFullYear()} Vector Engineering LLC. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

// Placeholder for any removed features
const FeatureDisabledPage = () => (
  <Box sx={{ textAlign: 'center', py: 8 }}>
    <Typography variant="h4" gutterBottom>
      Feature Not Available
    </Typography>
    <Typography variant="body1" paragraph>
      Video generation is not available in the current version.
    </Typography>
    <Typography variant="body1" paragraph>
      This application is focused on script creation and editing only.
    </Typography>
    <Button
      variant="contained"
      color="primary"
      onClick={() => window.history.back()}
    >
      Go Back
    </Button>
  </Box>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GenerationProvider>
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/create" element={<CreateScriptPage />} />
            <Route path="/create-script" element={<CreateScriptPage />} />
            <Route path="/scripts" element={<ScriptsPage />} />
            <Route path="/scripts/:scriptId" element={<ScriptDetailPage />} />
            <Route path="/characters" element={<CharacterViewerPage />} />
            <Route path="/generate-video/:scriptId" element={<FeatureDisabledPage />} />
            <Route path="/videos/:videoId" element={<FeatureDisabledPage />} />
          </Routes>
        </Layout>
      </GenerationProvider>
    </ThemeProvider>
  );
}

export default App;
